import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  BookOpen, Calendar, Eye, Tag, ChevronRight, ChevronLeft, Loader2,
  ArrowLeft, Search, Clock, Sparkles, Brain, CheckCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import NewsletterSignup from '../components/NewsletterSignup';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_IMAGES = {
  'Study Tips': 'https://images.unsplash.com/photo-1759984782199-a4f6d1b6054e?w=600&h=300&fit=crop',
  'EdTech': 'https://images.unsplash.com/photo-1620829813573-7c9e1877706f?w=600&h=300&fit=crop',
  'Productivity': 'https://images.unsplash.com/photo-1610484826625-ac2be7f1c8c1?w=600&h=300&fit=crop',
  'Wellness': 'https://images.unsplash.com/photo-1712762056200-50d8f803ba10?w=600&h=300&fit=crop',
  'Exam Prep': 'https://images.unsplash.com/photo-1758270705067-0d7edee57af0?w=600&h=300&fit=crop',
};

const BlogListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const page = parseInt(searchParams.get('page') || '1');
  const category = searchParams.get('category') || '';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = { page, limit: 9 };
        if (category) params.category = category;
        const [postsRes, catRes] = await Promise.all([
          axios.get(`${API}/blog/posts`, { params }),
          axios.get(`${API}/blog/categories`),
        ]);
        setPosts(postsRes.data.posts || []);
        setTotal(postsRes.data.total || 0);
        setPages(postsRes.data.pages || 1);
        setCategories(catRes.data.categories || []);
      } catch { setPosts([]); }
      setLoading(false);
    };
    load();
  }, [page, category]);

  return (
    <div className="f500-page max-w-6xl mx-auto" data-testid="blog-page">
      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--isp-heading)] tracking-tight">
          iStudyPro Blog
        </h1>
        <p className="text-[var(--isp-body)] mt-3 text-base sm:text-lg max-w-2xl mx-auto">
          Study tips, EdTech insights, and learning strategies to help you ace your exams
        </p>
      </div>

      {/* Category Pills */}
      {categories.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-3 mb-6" data-testid="blog-categories">
          <button
            onClick={() => setSearchParams({})}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              !category ? 'bg-[#002FA7] text-white' : 'bg-[var(--isp-glass)] text-[var(--isp-body)] border border-[var(--isp-glass-border)] hover:border-[#002FA7]/30'
            }`}
          >All Posts ({total})</button>
          {categories.map(c => (
            <button
              key={c.name}
              onClick={() => setSearchParams({ category: c.name })}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                category === c.name ? 'bg-[#002FA7] text-white' : 'bg-[var(--isp-glass)] text-[var(--isp-body)] border border-[var(--isp-glass-border)] hover:border-[#002FA7]/30'
              }`}
            >{c.name} ({c.count})</button>
          ))}
        </div>
      )}

      {/* Posts Grid */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16">
          <BookOpen className="w-12 h-12 mx-auto text-[var(--isp-muted)] mb-4" />
          <p className="text-lg text-[var(--isp-heading)] font-medium">No posts yet</p>
          <p className="text-sm text-[var(--isp-muted)]">Blog content is being generated. Check back soon!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {posts.map((post, i) => (
            <motion.div key={post.id || post.slug} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Link to={`/blog/${post.slug}`} className="block group" data-testid={`blog-card-${post.slug}`}>
                <div className="isp-card h-full flex flex-col hover:border-[#002FA7]/30 hover:-translate-y-1 transition-all overflow-hidden ">
                  {/* Cover Image */}
                  <div className="relative h-40 overflow-hidden bg-gradient-to-br from-[#002FA7]/10 to-blue-300/10">
                    <img
                      src={post.cover_image || CATEGORY_IMAGES[post.category] || CATEGORY_IMAGES['Study Tips']}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                    <span className="absolute top-3 left-3 text-[10px] px-2.5 py-1 rounded-full bg-white/90 text-[#002FA7] font-semibold backdrop-blur-sm">{post.category}</span>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h2 className="text-lg font-bold text-[var(--isp-heading)] mb-2 group-hover:text-[#002FA7] transition-colors line-clamp-2">{post.title}</h2>
                    <p className="text-sm text-[var(--isp-body)] line-clamp-2 flex-1">{post.excerpt}</p>
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-[var(--isp-divider)]">
                      <div className="flex items-center gap-2 text-[10px] text-[var(--isp-muted)]">
                        <Calendar className="w-3 h-3" />
                        {post.published_at ? new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] text-[var(--isp-muted)] flex items-center gap-1"><Eye className="w-2.5 h-2.5" /> {post.views || 0}</span>
                        <span className="text-xs text-[#002FA7] font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          Read <ChevronRight className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex justify-center gap-2 mb-8" data-testid="blog-pagination">
          <Button
            variant="outline" size="sm" disabled={page <= 1}
            onClick={() => setSearchParams({ page: page - 1, ...(category && { category }) })}
            className="rounded-xl"
          ><ChevronLeft className="w-4 h-4" /></Button>
          <span className="px-4 py-2 text-sm text-[var(--isp-body)]">Page {page} of {pages}</span>
          <Button
            variant="outline" size="sm" disabled={page >= pages}
            onClick={() => setSearchParams({ page: page + 1, ...(category && { category }) })}
            className="rounded-xl"
          ><ChevronRight className="w-4 h-4" /></Button>
        </div>
      )}

      {/* Newsletter + SEO Footer Block */}
      <div className="space-y-6 py-8 border-t border-[var(--isp-divider)]">
        <NewsletterSignup variant="card" source="blog" />
        <div className="text-center">
          <h2 className="text-lg font-bold text-[var(--isp-heading)] mb-2">Study Smarter with iStudyPro</h2>
          <p className="text-sm text-[var(--isp-body)] max-w-xl mx-auto mb-4">
            Join thousands of students using AI to record lectures, create flashcards, and ace exams. Start free today.
          </p>
          <Link to="/">
            <Button className="rounded-full bg-[#002FA7] hover:bg-[#00227A] px-6">Get Started Free</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};


const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [materials, setMaterials] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/blog/posts/${slug}`);
        setPost(res.data);
      } catch { setPost(null); }
      setLoading(false);
    };
    load();
  }, [slug]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div>;
  if (!post) return (
    <div className="text-center py-20">
      <p className="text-lg text-[var(--isp-heading)]">Post not found</p>
      <Link to="/blog" className="text-sm text-[#002FA7] hover:underline mt-2 inline-block">Back to Blog</Link>
    </div>
  );

  return (
    <article className="max-w-3xl mx-auto" data-testid="blog-post">
      <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-[#002FA7] hover:underline mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Blog
      </Link>

      <header className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs px-3 py-1 rounded-full bg-[#002FA7]/10 text-[#002FA7] font-medium">{post.category}</span>
          <span className="text-xs text-[var(--isp-muted)] flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {post.published_at ? new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
          </span>
          <span className="text-xs text-[var(--isp-muted)] flex items-center gap-1">
            <Eye className="w-3 h-3" /> {post.views} views
          </span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[var(--isp-heading)] leading-tight">{post.title}</h1>
        <p className="text-[var(--isp-body)] mt-3 text-lg">{post.excerpt}</p>
        {post.keywords && (
          <div className="flex flex-wrap gap-2 mt-4">
            {post.keywords.split(',').map(kw => (
              <span key={kw} className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--isp-glass)] border border-[var(--isp-glass-border)] text-[var(--isp-muted)]">
                {kw.trim()}
              </span>
            ))}
          </div>
        )}
      </header>

      <div className="isp-card p-6 sm:p-8 ">
        <div className="prose prose-lg max-w-none text-[var(--isp-body)] leading-relaxed" data-testid="blog-content">
          <ReactMarkdown
            components={{
              h2: ({children}) => <h2 className="text-xl font-bold text-[var(--isp-heading)] mt-6 mb-3">{children}</h2>,
              h3: ({children}) => <h3 className="text-lg font-semibold text-[var(--isp-heading)] mt-5 mb-2">{children}</h3>,
              p: ({children}) => <p className="text-[var(--isp-body)] mb-3 leading-relaxed">{children}</p>,
              ul: ({children}) => <ul className="list-disc pl-5 mb-3 space-y-1">{children}</ul>,
              ol: ({children}) => <ol className="list-decimal pl-5 mb-3 space-y-1">{children}</ol>,
              li: ({children}) => <li className="text-[var(--isp-body)]">{children}</li>,
              strong: ({children}) => <strong className="font-semibold text-[var(--isp-heading)]">{children}</strong>,
              a: ({href, children}) => <a href={href} className="text-[#002FA7] hover:underline">{children}</a>,
              blockquote: ({children}) => <blockquote className="border-l-4 border-[#002FA7]/30 pl-4 italic text-[var(--isp-muted)]">{children}</blockquote>,
            }}
          >
            {post.content}
          </ReactMarkdown>
        </div>
      </div>

      {/* CTA + Blog → Study Material Bridge */}
      <div className="mt-8 p-6  bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20" data-testid="blog-study-bridge">
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="flex-1 text-center sm:text-left">
            <h3 className="text-lg font-bold text-[var(--isp-heading)] flex items-center gap-2 justify-center sm:justify-start">
              <Sparkles className="w-5 h-5 text-amber-500" /> Turn This Into Study Material
            </h3>
            <p className="text-sm text-[var(--isp-body)] mt-1">
              AI will generate flashcards and a study plan from this article's key concepts.
            </p>
          </div>
          <Button
            onClick={async () => {
              setGenerating(true);
              try {
                const res = await axios.post(`${API}/blog/posts/${slug}/generate-study-material`);
                setMaterials(res.data);
                toast.success(`${res.data.flashcards_created} flashcards created!`);
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to generate. Please sign in first.');
              }
              setGenerating(false);
            }}
            disabled={generating || !!materials}
            className="rounded-xl gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-6 shrink-0"
            data-testid="try-this-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : materials ? <CheckCircle className="w-4 h-4" /> : <Brain className="w-4 h-4" />}
            {generating ? 'Generating...' : materials ? 'Materials Created!' : 'Try This in iStudyPro'}
          </Button>
        </div>

        {/* Generated Materials */}
        {materials && (
          <div className="mt-5 space-y-4" data-testid="generated-materials">
            {/* Key Takeaways */}
            {materials.key_takeaways?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-[var(--isp-heading)] mb-2">Key Takeaways</h4>
                <ul className="space-y-1">
                  {materials.key_takeaways.map((t, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[var(--isp-body)]">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Flashcards Preview */}
            {materials.flashcards?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-[var(--isp-heading)] mb-2">
                  Flashcards ({materials.flashcards_created} saved to your library)
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {materials.flashcards.slice(0, 4).map((fc, i) => (
                    <div key={i} className="p-3 rounded-xl bg-[var(--isp-glass)] border border-[var(--isp-glass-border)]" data-testid={`flashcard-${i}`}>
                      <p className="text-xs font-medium text-[#002FA7] mb-1">Q:</p>
                      <p className="text-sm text-[var(--isp-heading)]">{fc.front}</p>
                      <p className="text-xs font-medium text-emerald-500 mt-2 mb-1">A:</p>
                      <p className="text-xs text-[var(--isp-body)]">{fc.back}</p>
                    </div>
                  ))}
                </div>
                {materials.flashcards.length > 4 && (
                  <p className="text-[10px] text-[var(--isp-muted)] mt-1">+{materials.flashcards.length - 4} more flashcards saved</p>
                )}
              </div>
            )}

            {/* Study Plan */}
            {materials.study_plan?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-[var(--isp-heading)] mb-2">Suggested Study Plan</h4>
                <div className="space-y-1">
                  {materials.study_plan.map((step, i) => (
                    <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-[var(--isp-glass-inner)]">
                      <span className="w-6 h-6 rounded-full bg-[#002FA7]/10 text-[#002FA7] text-xs font-bold flex items-center justify-center shrink-0">
                        {step.day}
                      </span>
                      <span className="text-sm text-[var(--isp-body)] flex-1">{step.task}</span>
                      <span className="text-[10px] text-[var(--isp-muted)]">{step.duration_minutes}min</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Link to="/study-plan" className="text-xs text-[#002FA7] hover:underline flex items-center gap-1">
              View all study materials <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        )}
      </div>

      {/* Secondary CTA */}
      <div className="mt-4 p-6  bg-gradient-to-r from-[#002FA7]/10 to-blue-600/10 border border-[#002FA7]/20 text-center">
        <h3 className="text-xl font-bold text-[var(--isp-heading)] mb-2">Ready to Study Smarter?</h3>
        <p className="text-sm text-[var(--isp-body)] mb-4">
          iStudyPro uses AI to transcribe your lectures, create flashcards, and help you ace your exams.
        </p>
        <Link to="/">
          <Button className="rounded-full bg-[#002FA7] hover:bg-[#00227A] px-8 py-3 text-base">
            Try iStudyPro Free
          </Button>
        </Link>
      </div>

      {/* Newsletter */}
      <div className="mt-6">
        <NewsletterSignup variant="card" source="blog_post" />
      </div>

      {/* Related: back to blog */}
      <div className="mt-6 text-center">
        <Link to="/blog" className="text-sm text-[#002FA7] hover:underline flex items-center gap-1 justify-center">
          <ArrowLeft className="w-3 h-3" /> More Articles
        </Link>
      </div>
    </article>
  );
};

export { BlogListPage, BlogPostPage };
